import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { type ReactElement, type ReactNode, useId } from 'react';

import { NUMERIC_FONT } from '../../app/theme';

/** Style of every number field of E7: the numeric font at 16 px (no zoom on iOS focus). */
export const numericInputSx = { '& input': { fontFamily: NUMERIC_FONT, fontSize: 16 } } as const;

/** Card of one E7 section with its uppercase heading (mockup 6 `.card` and `.h`). */
export function SettingsSection({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): ReactElement {
  const titleId = useId();
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby={titleId}
      sx={{ borderRadius: '14px', p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Typography
        id={titleId}
        component="h2"
        sx={{
          m: 0,
          fontSize: 12,
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

/** Bold option title over its muted explanation (mockup 6 `.opt` label). */
export function OptionText({
  title,
  hint,
  aside,
}: {
  readonly title: string;
  readonly hint?: ReactNode;
  readonly aside?: ReactNode;
}): ReactElement {
  return (
    <Box component="span" sx={{ display: 'block' }}>
      <Box component="strong" sx={{ fontWeight: 600 }}>
        {title}
      </Box>
      {aside}
      {hint !== undefined && (
        <Box
          component="span"
          sx={{ display: 'block', fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}
        >
          {hint}
        </Box>
      )}
    </Box>
  );
}

/**
 * One option of a radio group: the radio, then its bold title, an optional badge and its muted
 * explanation (mockup 6 `.opt`). Used by the size and execution cards.
 */
export function OptionRadio({
  value,
  title,
  hint,
  aside,
}: {
  readonly value: string;
  readonly title: string;
  readonly hint: ReactNode;
  readonly aside?: ReactNode;
}): ReactElement {
  return (
    <FormControlLabel
      value={value}
      control={<Radio />}
      sx={{ alignItems: 'flex-start', mx: 0, gap: 0.5 }}
      label={<OptionText title={title} hint={hint} aside={aside} />}
    />
  );
}

/**
 * A labelled switch, label first and switch at the end (mockup 6). Exposed as `role="switch"`
 * so assistive technologies announce on/off, not checked.
 */
export function SwitchRow({
  checked,
  onChange,
  label,
}: {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: ReactNode;
}): ReactElement {
  return (
    <FormControlLabel
      labelPlacement="start"
      sx={{ mx: 0, alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}
      control={
        <Switch
          checked={checked}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
          slotProps={{ input: { role: 'switch' } }}
        />
      }
      label={label}
    />
  );
}
